import configData from "../config.json";
import { authHeader, handleResponse } from '../_helpers/index';

export const marketService = {
    getPublished,
    getUnpublished,
    getMarketDetails,
    createMarket,
    updateMarketAfterConditionCreation,
    updateMarketAfterMarketCreation,
    ensureDeterministicFactorSalt,
    updateCreateConditionConfirmed,
    updateCreateMarketConfirmed,
    updateMarketAfterInitialFunding,
    updateInitialFundingConfirmed,
    updateMarketAfterResolution,
    updateResolveMarketConfirmed,
    updateMarket,
    updateMarketAfterUmaQuestionCreation,
    updateRealityQuestionConfirmed,
    updateOrder
};

function getPublished() {
    const requestOptions = { method: 'GET', headers: authHeader() };
    return fetch(`${configData.API_URL}/markets/get/published`, requestOptions).then(handleResponse);
}

function getUnpublished() {
    const requestOptions = { method: 'GET', headers: authHeader() };
    return fetch(`${configData.API_URL}/markets/get/unpublished`, requestOptions).then(handleResponse);
}

function getMarketDetails(id){
    const requestOptions = { method: 'GET', headers: authHeader() };
    return fetch(`${configData.API_URL}/market/detail/`+id, requestOptions).then(handleResponse);
}

function createMarket(
    {
        question, 
        ancillary_data, 
        reward_token, 
        reward, 
        proposal_bond, 
        liveness, 
        option_1, 
        option_2, 
        market_ends_on, 
        about_market, 
        // oracle, 
        resolution_source, 
        option_1_odd, 
        option_2_odd, 
        permalink, 
        category, 
        image_url,
        image_file,
        initial_funding_amount
    }
) {
    const oracle = configData.addresses.conditionResolverUsingReality;
    const formData = new FormData();

    formData.append('question', question);
    formData.append('option_1', option_1);
    formData.append('option_2', option_2);
    formData.append('about_market', about_market);
    formData.append('oracle', oracle);
    formData.append('resolution_source', resolution_source || '');
    formData.append('option_1_odd', option_1_odd);
    formData.append('option_2_odd', option_2_odd);
    formData.append('permalink', permalink);
    formData.append('image_url', image_url || '');
    formData.append('initial_funding_amount', initial_funding_amount);
    formData.append('ancillary_data', ancillary_data);
    formData.append('reward_token', reward_token);
    formData.append('reward', reward);
    formData.append('proposal_bond', proposal_bond);
    formData.append('market_ends_on', market_ends_on);
    formData.append('category', category);
    formData.append('liveness', liveness);

    if (image_file instanceof File) {
        formData.append('image_file', image_file);
    }

    const { Authorization } = authHeader();
    const requestOptions = {
        method: 'POST',
        headers: Authorization ? { Authorization } : {},
        body: formData
    };

    return fetch(`${configData.API_URL}/market/create`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateMarketAfterConditionCreation(id, receiptFromBlockChain, collectionIds, positionIds) {
    var blockChainReturnValues = receiptFromBlockChain.events.ConditionPreparation.returnValues;
    var dataToBeSent = {
        id, 
        condition_id: blockChainReturnValues.conditionId,
        collection_ids: collectionIds,
        position_ids: positionIds,
        blockchain_response: receiptFromBlockChain
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-market-after-condition-creation`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateMarketAfterMarketCreation(id, receiptFromBlockChain, deterministicFactorSalt) {
    var blockChainReturnValues = receiptFromBlockChain.events.FixedProductMarketMakerCreation.returnValues;
    var dataToBeSent = {
        id, 
        fpmm_market_maker_address: blockChainReturnValues.fixedProductMarketMaker,
        blockchain_response: receiptFromBlockChain,
        deterministic_factor_salt: deterministicFactorSalt
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-market-after-market-linking`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function ensureDeterministicFactorSalt(marketId) {
    var dataToBeSent = {
        market_id: marketId
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/ensure-deterministic-factor-salt`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateMarketAfterUmaQuestionCreation(id, receiptFromBlockChain, conditionId, collectionIds, positionIds) {
    var question_id = receiptFromBlockChain.events.QuestionInitialized.returnValues.questionID;
    var dataToBeSent = {
        id, 
        question_id : question_id,
        condition_id: conditionId,
        collection_ids: collectionIds,
        position_ids: positionIds,
        blockchain_response: receiptFromBlockChain
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-market-after-question-creation`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateMarketAfterInitialFunding(id, receiptFromBlockChain) {
    var blockChainReturnValues = receiptFromBlockChain.events.FPMMFundingAdded.returnValues;
    var dataToBeSent = {
        id, 
        blockchain_response: receiptFromBlockChain
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-market-after-initial-funding`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}
function updateMarketAfterResolution(id, receiptFromBlockChain, resolutionSubmitted) {
    var dataToBeSent = {
        id, 
        blockchain_response: receiptFromBlockChain,
        resolution_submitted: resolutionSubmitted
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-market-after-resolution`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateResolveMarketConfirmed(marketId, confirmed) {
    var dataToBeSent = {
        market_id: marketId,
        confirmed
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-resolve-market-confirmed`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateRealityQuestionConfirmed(marketId, confirmed) {
    var dataToBeSent = {
        market_id: marketId,
        confirmed
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-market-reality-question-confirmed`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateCreateConditionConfirmed(marketId, confirmed) {
    var dataToBeSent = {
        market_id: marketId,
        confirmed
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-blockchain-condition-confirmed`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateCreateMarketConfirmed(marketId, confirmed) {
    var dataToBeSent = {
        market_id: marketId,
        confirmed
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-blockchain-market-confirmed`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateInitialFundingConfirmed(marketId, confirmed) {
    var dataToBeSent = {
        market_id: marketId,
        confirmed
    };
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(dataToBeSent)
    };

    return fetch(`${configData.API_URL}/market/update-initial-funding-confirmed`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateMarket(marketId, aboutMarket, news, imageUrl, imageFile) {
    const formData = new FormData();
    formData.append('market_id', marketId);
    formData.append('about_market', aboutMarket);
    formData.append('news', JSON.stringify(news));

    if (imageUrl && imageUrl.trim()) {
        formData.append('image_url', imageUrl.trim());
    }

    if (imageFile instanceof File) {
        formData.append('image_file', imageFile);
    }

    const { Authorization } = authHeader();
    const requestOptions = {
        method: 'POST',
        headers: Authorization ? { Authorization } : {},
        body: formData
    };

    return fetch(`${configData.API_URL}/market/update-market`, requestOptions)
        .then(handleResponse)
        .then(market => {
            return market;
        });
}

function updateOrder(id, order) {
    const requestOptions = {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ order })
    };
    return fetch(`${configData.API_URL}/markets/${id}/order`, requestOptions).then(handleResponse);
}
